import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { DSButton, DSPageHeader, DSInput, DSCheckbox } from '../design-system/primitives';
import { authService } from '../services/authService';
import { matchesName } from '../utils/signatureName';
import './Dashboard.css';
import './AcknowledgeRules.css';

// Signing screen for the documents a member agrees to: the club rules and the
// membership agreement. Both are presented at application time and both can be
// revised, so both can come back around.
//
// One screen rather than two, working through whatever is outstanding in turn.
// A member who is behind on both should sign both in one sitting instead of
// being bounced to a second blocking screen the moment they finish the first.

function AcknowledgeRules({ isRequired = false, onAcknowledged }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Opening a specific document from the profile, to read what you signed
  // rather than to be asked for a signature.
  const requestedDocument = searchParams.get('document');
  const [queue, setQueue] = useState([]);
  const [doc, setDoc] = useState(null);
  const [signature, setSignature] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [signedCount, setSignedCount] = useState(0);
  // How far through the document they have scrolled. Signing is held until the
  // end is reached - the point of the screen is that somebody read it, and a
  // sign button available immediately says the opposite.
  const [readPct, setReadPct] = useState(0);
  const contentRef = useRef(null);

  const currentUser = authService.getCurrentUser() || {};
  const expectedName = [currentUser.firstName, currentUser.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const signatureMatches = matchesName(signature, currentUser);

  const loadDocument = useCallback(async (key) => {
    const response = await apiService.getDocument(key);
    setDoc(response.data);
    setSignature('');
    setAgreed(false);
  }, []);

  // The signed-in user is cached in localStorage, and the app seeds its blocking
  // state from that cache on first render so there is no flash before the
  // profile refresh lands. That means the cache has to be corrected the moment
  // something is signed - otherwise a refresh re-reads `required: true` and
  // presents the screen again for a document that has already been signed.
  const syncCachedUser = useCallback((outstanding) => {
    const cached = authService.getCurrentUser();
    if (!cached) return;
    authService.setCurrentUser({
      ...cached,
      documentAcknowledgment: { required: outstanding.length > 0, documents: outstanding }
    });
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const status = await apiService.getDocumentStatus();
      const outstanding = status.data?.documents || [];
      setQueue(outstanding);
      syncCachedUser(outstanding);

      if (outstanding.length === 0) {
        // Nothing to sign. Release the app-wide block if it is still set, and
        // show a plain confirmation rather than a signing form that would only
        // fail. Presenting the form here is what produced "already been
        // acknowledged" as an error message.
        if (onAcknowledged) onAcknowledged();

        // Unless they asked for a specific document, in which case show it -
        // "tapping a document opens what you signed".
        if (requestedDocument) {
          await loadDocument(requestedDocument);
          return;
        }

        setDoc(null);
        return;
      }

      // A document named in the URL wins, so tapping the second row on the
      // profile does not silently open the first.
      const first = requestedDocument
        && outstanding.some((d) => d.documentKey === requestedDocument)
        ? requestedDocument
        : outstanding[0].documentKey;
      await loadDocument(first);
    } catch (err) {
      // The signature is still owed - the database says so and that does not
      // change. But nobody can be asked to agree to something the app cannot
      // show them, and leaving the block set traps them: every navigation
      // redirects straight back to a screen that will not render.
      //
      // So release the block for this session and explain. They are asked again
      // next time they sign in, by which point the document should load.
      console.error('Could not load the document to sign:', err);
      setError('unavailable');
      setDoc(null);
      if (onAcknowledged) onAcknowledged();
    } finally {
      setLoading(false);
    }
    // onAcknowledged is stable in practice; excluded so a parent re-render does
    // not re-run this and refetch on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDocument, syncCachedUser, requestedDocument]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset progress whenever a different document is shown, so finishing the
  // first one does not count as having read the second.
  useEffect(() => {
    setReadPct(0);
  }, [doc?.key]);

  const handleScroll = (event) => {
    const el = event.currentTarget;
    const max = el.scrollHeight - el.clientHeight;
    // A document short enough not to scroll counts as read: there is nothing to
    // scroll to, and holding the button hostage to an impossible gesture would
    // be a dead end.
    setReadPct(max > 40 ? Math.min(1, el.scrollTop / max) : 1);
  };

  const skipToEnd = () => {
    const el = contentRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agreed) {
      setError('You must agree before continuing');
      return;
    }
    if (!signature.trim()) {
      setError('Please provide your electronic signature');
      return;
    }
    if (!signatureMatches) {
      setError(`Please sign with your full name, ${expectedName}`);
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const response = await apiService.acknowledgeDocument({
        documentKey: doc.key,
        signature: signature.trim()
      });

      const remaining = response.data?.outstanding || [];
      setSignedCount((n) => n + 1);
      setQueue(remaining);
      syncCachedUser(remaining);

      if (remaining.length > 0) {
        // Straight on to the next one, rather than letting them out and
        // blocking them again on the next navigation.
        await loadDocument(remaining[0].documentKey);
        setSubmitting(false);
        return;
      }

      // Clear the app-wide block before navigating, or the redirect that sent
      // them here would send them straight back.
      if (onAcknowledged) onAcknowledged();
      navigate('/my-membership');
    } catch (err) {
      // 409 means it is already signed. That is the state we were trying to
      // reach, so it is not a failure to show somebody - it usually means a
      // duplicate submit or a stale tab. Re-read the truth and carry on.
      if (err.response?.status === 409) {
        setError('');
        setSubmitting(false);
        await load();
        return;
      }

      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="page"><div className="loading">Loading...</div></div>;
  }

  // Nothing outstanding. Reached by signing everything, or by navigating here
  // directly, or by refreshing on a stale tab. None of those is an error, so
  // none of them gets an error message.
  if (!doc && !error) {
    return (
      <div className="page">
        <DSPageHeader
          title="You are all signed up"
          description="There is nothing waiting for your signature."
        />
        <div className="card">
          <p style={{ marginTop: 0 }}>
            You have signed the current club rules and membership agreement. If
            either changes, you will be asked to read and sign it again.
          </p>
          <DSButton
            appearance="solid"
            tone="primary"
            onClick={() => navigate('/my-membership')}
          >
            Back to my membership
          </DSButton>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="page">
        <DSPageHeader
          title="We could not load the document"
          description="Nothing is wrong with your account."
        />
        <div className="card">
          <p style={{ marginTop: 0 }}>
            There is a document waiting for your signature, but we could not
            fetch it just now. You can carry on using the app, and we will ask
            again next time you sign in. If it keeps happening, let an admin
            know.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <DSButton appearance="solid" tone="primary" onClick={() => navigate('/my-membership')}>
              Continue
            </DSButton>
            <DSButton appearance="outline" tone="neutral" onClick={load}>
              Try again
            </DSButton>
          </div>
        </div>
      </div>
    );
  }

  const isReacknowledgment = Boolean(doc.hasEverAcknowledged);
  const changes = Array.isArray(doc.changeSummary) ? doc.changeSummary : [];
  // Nothing to sign for this document: they opened it from their profile to
  // read what they agreed to.
  const readOnly = !doc.required;
  const atEnd = readPct > 0.985;
  const total = queue.length + signedCount;
  const position = signedCount + 1;

  return (
    <div className="page">
      <DSPageHeader
        title={isReacknowledgment ? `${doc.label} has been updated` : doc.title}
        description={
          isReacknowledgment
            ? 'Please read the current version and sign again to continue.'
            : 'Please read this and sign to continue.'
        }
      />

      {/* Only when there is more than one, so a single document does not get an
          unnecessary "1 of 1". */}
      {total > 1 && (
        <p style={{ color: 'var(--ds-color-textMuted)', fontSize: '14px', marginTop: '-8px' }}>
          Document {position} of {total}
        </p>
      )}

      <div className="card">
        <div style={{
          backgroundColor: 'var(--ds-color-statusWarningBg)',
          color: 'var(--ds-color-statusWarningText)',
          padding: '15px',
          borderRadius: 'var(--ds-radius-card)',
          marginBottom: '20px'
        }}>
          {isReacknowledgment ? (
            <>
              <strong>Why am I seeing this?</strong> This document changed
              {doc.effectiveDate
                ? ` on ${new Date(`${doc.effectiveDate}T00:00:00`).toLocaleDateString()}`
                : ''}
              , so every member is being asked to read it again. Your membership
              is not affected.
            </>
          ) : (
            <>
              <strong>Important:</strong> Please read this carefully. Your electronic
              signature confirms that you understand and agree to it.
            </>
          )}

          {/* What actually changed, in the admin's words.
              "This changed, sign it again" with no indication of what changed
              teaches people to scroll to the bottom and sign. Three bullets is
              the difference between a re-signature that means something and one
              that does not. */}
          {isReacknowledgment && changes.length > 0 && (
            <div className="ack-changes">
              <p className="ack-changes__title">What changed</p>
              {changes.map((change, index) => (
                <div className="ack-changes__row" key={`${change.kind}-${index}`}>
                  <span className={`ack-changes__tag ack-changes__tag--${change.kind}`}>
                    {change.kind}
                  </span>
                  <span className="ack-changes__text">{change.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <p style={{ fontSize: '14px', color: 'var(--ds-color-textMuted)', marginBottom: '20px' }}>
          Updated {new Date(doc.modified).toLocaleDateString()}
          {doc.acknowledgedAt && (
            <> | You last signed on {new Date(doc.acknowledgedAt).toLocaleDateString()}</>
          )}
        </p>

        {/* surfaceMuted, not textDefault. This was set to the default TEXT
            colour, which painted a near-black panel behind near-black text -
            the content everybody is required to read. */}
        <div
          ref={contentRef}
          onScroll={handleScroll}
          className="ack-document"
          dangerouslySetInnerHTML={{ __html: doc.content }}
        />

        {/* Progress, and a way past it. The gate is there so the sign button is
            not available before the document has been seen, not to trap
            somebody who has read it elsewhere or is coming back to it. */}
        {!readOnly && !atEnd && (
          <div className="ack-readbar">
            <span className="ack-readbar__label">
              {readPct < 0.05 ? 'Read to the end to sign' : `${Math.round(readPct * 100)}% read`}
            </span>
            <DSButton appearance="solid" tone="primary" size="sm" onClick={skipToEnd}>
              Skip to the end
            </DSButton>
          </div>
        )}

        {readOnly && (
          <div className="ack-signed-note">
            You signed this on {new Date(doc.acknowledgedAt).toLocaleDateString()}. There is
            nothing to do here; we will ask again only if the club changes it.
          </div>
        )}

        {error && <div className="alert alert-error" style={{ marginBottom: '20px' }}>{error}</div>}

        {!readOnly && (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <DSCheckbox
              id="agree-to-document"
              checked={agreed}
              disabled={submitting}
              onChange={(e) => setAgreed(e.target.checked)}
              label={`I have read and agree to the ${doc.label.toLowerCase()}. I understand it may change over time and it is my responsibility to stay updated.`}
            />
          </div>

          {/* No style overrides. DSInput spreads props onto the inner <input>,
              which already sits inside .ds-input-shell - the shell draws the
              border, so passing one here painted a second border inside the
              first. */}
          <div style={{ marginBottom: '20px' }}>
            <DSInput
              id="signature"
              label="Electronic signature"
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder={expectedName || 'Type your full name'}
              disabled={submitting}
              // Signing is meant to be a deliberate act, not a form field the
              // browser fills in for you. Chrome does not always honour this on
              // a name-shaped input, which is why the design system also has to
              // survive being autofilled.
              autoComplete="off"
              state={signature.trim() && !signatureMatches ? 'error' : 'inactive'}
              helpText={
                signature.trim() && !signatureMatches
                  ? `Please sign with your full name, ${expectedName}.`
                  : 'Typing your name is a legally binding electronic signature.'
              }
              required
            />
          </div>

          <div style={{
            backgroundColor: 'var(--ds-color-statusInfoBg)',
            color: 'var(--ds-color-statusInfoText)',
            padding: '15px',
            borderRadius: 'var(--ds-radius-card)',
            marginBottom: '20px',
            fontSize: '13px'
          }}>
            <strong>Legal Notice:</strong> Your acknowledgment will be timestamped and
            stored securely. This serves as a legally binding agreement.
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {/* No way out when this is required. Offering Cancel on a blocking
                screen either does nothing or bounces straight back here. */}
            {!isRequired && (
              <DSButton
                type="button"
                appearance="outline"
                tone="neutral"
                onClick={() => navigate('/my-membership')}
                disabled={submitting}
              >
                Cancel
              </DSButton>
            )}
            <DSButton
              type="submit"
              appearance="solid"
              tone="primary"
              disabled={submitting || !agreed || !signature.trim() || !signatureMatches || !atEnd}
            >
              {submitting
                ? 'Submitting...'
                : !atEnd
                  ? 'Read to the end to sign'
                  : queue.length > 1 ? 'Sign and go to the next one' : 'Sign and continue'}
            </DSButton>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

export default AcknowledgeRules;
